import { useMutation } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import { LogoutIcon } from './line-icons';
import { authApi } from '../../api/auth';
import { clearSession } from '../../auth/session';

/**
 * Sits at the foot of the side rail and follows its two forms: labelled at
 * 240px, icon-only at 64px. Collapsed it carries its name in `aria-label` and a
 * tooltip, since the visible label is gone; expanded the label is the
 * accessible name and a tooltip would only repeat it.
 *
 * Both channels switch to "Signing out…" while the request is in flight — with
 * the button disabled, the label is the only status left.
 */
export const AdminLogoutButton = ({ expanded = true }: { expanded?: boolean }) => {
  const navigate = useNavigate();

  const { mutate, isPending } = useMutation({
    mutationFn: authApi.logout,
    onSettled: async () => {
      clearSession();
      await navigate({ to: '/login' });
    },
  });

  const label = isPending ? 'Signing out…' : 'Logout';

  const button = (
    <Button
      type="button"
      variant="text"
      aria-label={expanded ? undefined : label}
      disabled={isPending}
      onClick={() => mutate()}
      data-testid="admin-logout-button"
      startIcon={expanded ? <LogoutIcon /> : undefined}
      sx={(theme) => {
        const { navRail } = theme.tokens;
        return {
          minWidth: 0,
          justifyContent: expanded ? 'flex-start' : 'center',
          padding: navRail.itemPadding,
          borderRadius: `${theme.tokens.radius.pill}px`,
          fontSize: navRail.labelSize,
          fontWeight: navRail.labelWeight,
          letterSpacing: navRail.labelTracking,
          color: theme.tokens.color.ink2,
          '& .MuiButton-startIcon': {
            marginLeft: 0,
            marginRight: `${navRail.itemGap}px`,
            '& > *:first-of-type': { fontSize: navRail.iconSize },
          },
          '&:hover': { bgcolor: theme.tokens.color.bg, color: theme.tokens.color.ink },
        };
      }}
    >
      {expanded ? label : <LogoutIcon sx={{ fontSize: (t) => t.tokens.navRail.iconSize }} />}
    </Button>
  );

  if (expanded) {
    return button;
  }

  // Collapsed, the tooltip goes straight on the button wherever it can. MUI
  // names its own child with `aria-label`, and a bare <span> is role=generic —
  // which ARIA prohibits naming, the same trap the disabled header action was
  // fixed for.
  if (!isPending) {
    return (
      <Tooltip title={label} placement="right">
        {button}
      </Tooltip>
    );
  }

  // In flight the button is disabled and emits no pointer or focus events, so
  // the tooltip needs a live wrapper. It carries the button role and
  // `aria-disabled` for the same reason the header action's does, with the
  // inner button hidden so the pair reads as one control.
  return (
    <Tooltip title={label} placement="right">
      <span
        role="button"
        aria-disabled="true"
        tabIndex={0}
        aria-label={label}
        style={{ display: 'inline-flex' }}
      >
        <span aria-hidden="true" style={{ display: 'inline-flex' }}>
          {button}
        </span>
      </span>
    </Tooltip>
  );
};
